import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { cases, heroImages } from "../data/cases";
import FadeIn from "../components/FadeIn";
import Img from "../components/Img";
import SEO from "../components/SEO";
import { seoByPath } from "../lib/seo";

const Home = () => {
    const { t, lang } = useLang();

    const featured = cases.slice(0, 3);
    const s = seoByPath["/"];

    return (
        <main data-testid="page-home" className="bg-white text-[#111111]">
            <SEO
                title={s.title}
                description={s.description}
                path="/"
                ogImage={s.ogImage}
                type={s.type}
            />
            {/* HERO — classic full-bleed with overlay text + scroll cue */}
            <section
                className="relative bg-[#0F0F0E] h-screen min-h-[640px] w-full overflow-hidden"
            >
                <motion.div
                    initial={{ scale: 1.08, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0"
                >
                    <Img
                        priority
                        src={heroImages.diningHall}
                        alt="MM Byggeri & Rådgivning"
                        className="w-full h-full object-cover"
                    />
                </motion.div>
                <div className="absolute inset-0 hero-vignette" />
                <div className="absolute inset-0 bg-black/15" />

                <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-between pt-28 md:pt-32 pb-10 md:pb-14 text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="text-[11px] tracking-luxury uppercase"
                    >
                        {t.home.eyebrow}
                    </motion.div>

                    <div>
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.92] tracking-tight max-w-5xl"
                            data-testid="hero-headline"
                        >
                            {t.home.heroLine1}
                            <br />
                            <span className="italic font-light">
                                {t.home.heroLine2}
                            </span>
                            <br />
                            {t.home.heroLine3}
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-10 md:mt-14 max-w-2xl text-base md:text-lg font-light text-white/85 leading-[1.75] tracking-[0.01em]"
                        >
                            {t.home.heroSubtitle}
                        </motion.p>
                    </div>

                    <motion.button
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.3 }}
                        onClick={() =>
                            window.scrollTo({ top: window.innerHeight, behavior: "smooth" })
                        }
                        className="flex items-center gap-3 text-[11px] tracking-luxury uppercase self-start group"
                        data-testid="hero-scroll-cue"
                    >
                        <ArrowDown
                            size={14}
                            className="transition-transform duration-500 group-hover:translate-y-1"
                        />
                        <span>{t.home.scrollHint}</span>
                    </motion.button>
                </div>
            </section>

            {/* POSITIONING */}
            <section className="bg-white py-32 md:py-48">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-3">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.home.positioningLabel}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-9">
                        <p
                            data-testid="positioning-statement"
                            className="font-serif font-light text-3xl md:text-5xl leading-[1.1] tracking-tight text-[#111]"
                        >
                            {t.home.positioning}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* USPs — Hvad gør os unikke */}
            <section className="bg-white pb-24 md:pb-32 border-t border-[#EAE8E4] pt-24 md:pt-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="mb-16 md:mb-20 grid grid-cols-12 gap-6">
                        <div className="col-span-12 md:col-span-3">
                            <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                                {t.home.uspLabel}
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-9">
                            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl whitespace-pre-line">
                                {t.home.uspTitle}
                            </h2>
                        </div>
                    </FadeIn>
                    <div className="space-y-0">
                        {[t.home.usp1, t.home.usp2, t.home.usp3].map((u, i) => (
                            <FadeIn
                                key={i}
                                delay={i * 0.06}
                                className="grid grid-cols-12 gap-6 py-10 md:py-14 border-t border-[#EAE8E4]"
                                data-testid={`usp-${i + 1}`}
                            >
                                <div className="col-span-2 md:col-span-1 editorial-number text-2xl md:text-3xl not-italic">
                                    0{i + 1}
                                </div>
                                <p className="col-span-10 md:col-span-10 md:col-start-3 font-serif font-light text-2xl md:text-4xl leading-[1.2] tracking-tight max-w-4xl">
                                    {u}
                                </p>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* SELECTED CASES */}
            <section className="bg-[#F9F8F6] py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="flex items-end justify-between flex-wrap gap-6 mb-16 md:mb-24">
                        <div>
                            <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                {t.home.selectedWorkLabel}
                            </div>
                            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1] tracking-tight max-w-3xl">
                                {t.home.selectedWorkTitle}
                            </h2>
                        </div>
                        <Link
                            to="/cases"
                            data-testid="home-view-all-cases"
                            className="text-[11px] tracking-luxury uppercase link-underline inline-flex items-center gap-2"
                        >
                            {t.home.viewAll} <ArrowRight size={14} />
                        </Link>
                    </FadeIn>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {featured.map((c, i) => (
                            <FadeIn
                                key={c.slug}
                                delay={i * 0.08}
                            >
                                <Link
                                    to={`/cases/${c.slug}`}
                                    data-testid={`home-case-${c.slug}`}
                                    className="block group"
                                >
                                    <div className="case-card aspect-[4/5] w-full bg-[#EBE8E3]">
                                        <Img
                                            sizes="(min-width: 768px) 50vw, 100vw"
                                            src={c.hero}
                                            alt={c.title[lang]}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="mt-5 flex items-baseline justify-between gap-6">
                                        <div>
                                            <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                                                {c.location[lang]}  ·  {c.year}
                                            </div>
                                            <h3 className="mt-1 font-serif text-2xl md:text-3xl">
                                                {c.title[lang]}
                                            </h3>
                                        </div>
                                        <div className="text-[11px] tracking-luxury uppercase opacity-60 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                            {t.cases.viewProject} →
                                        </div>
                                    </div>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES — Renovering / Tilbygning / Sommerhus */}
            <section className="bg-white py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="mb-16 md:mb-20 grid grid-cols-12 gap-6">
                        <div className="col-span-12 md:col-span-3">
                            <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                                {t.home.servicesLabel}
                            </div>
                        </div>
                        <div className="col-span-12 md:col-span-9">
                            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl whitespace-pre-line">
                                {t.home.servicesTitle}
                            </h2>
                        </div>
                    </FadeIn>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
                        {[
                            { title: t.home.s1Title, text: t.home.s1Text },
                            { title: t.home.s2Title, text: t.home.s2Text },
                            { title: t.home.s3Title, text: t.home.s3Text },
                        ].map((s, i) => (
                            <FadeIn
                                key={i}
                                delay={i * 0.08}
                                className="border-t border-[#D9D4CD] pt-8"
                                data-testid={`service-${i + 1}`}
                            >
                                <div className="editorial-number text-base not-italic mb-4">
                                    0{i + 1}
                                </div>
                                <h3 className="font-serif text-3xl md:text-4xl font-light mb-5">
                                    {s.title}
                                </h3>
                                <p className="text-sm md:text-base text-[#555] leading-relaxed md:leading-relaxed max-w-sm">
                                    {s.text}
                                </p>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* AUDIENCE SPLIT */}
            <section className="bg-white py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <FadeIn className="mb-16 md:mb-20">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            {t.home.audienceLabel}
                        </div>
                    </FadeIn>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#EAE8E4]">
                        <FadeIn className="bg-white">
                            <Link
                                to="/privat"
                                data-testid="home-card-private"
                                className="block group p-8 md:p-16 h-full"
                            >
                                <div className="aspect-[4/3] w-full overflow-hidden mb-10">
                                    <Img
                                        sizes="(min-width: 768px) 50vw, 100vw"
                                        src={heroImages.brassTap}
                                        alt="Privat"
                                        className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                                    />
                                </div>
                                <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                    01 / Privat
                                </div>
                                <h3 className="font-serif text-3xl md:text-5xl font-light leading-[1.05] mb-5">
                                    {t.home.forPrivateTitle}
                                </h3>
                                <p className="text-sm md:text-base text-[#555] leading-relaxed md:leading-relaxed max-w-md mb-8">
                                    {t.home.forPrivateText}
                                </p>
                                <div className="text-[11px] tracking-luxury uppercase link-underline inline-flex items-center gap-2">
                                    {t.home.forPrivateCta} <ArrowRight size={14} />
                                </div>
                            </Link>
                        </FadeIn>

                        <FadeIn delay={0.1} className="bg-white">
                            <Link
                                to="/arkitekter"
                                data-testid="home-card-arch"
                                className="block group p-8 md:p-16 h-full"
                            >
                                <div className="aspect-[4/3] w-full overflow-hidden mb-10">
                                    <Img
                                        sizes="(min-width: 768px) 50vw, 100vw"
                                        src={heroImages.kystParquet}
                                        alt="Arkitekter"
                                        className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                                    />
                                </div>
                                <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-3">
                                    02 / Arkitekter
                                </div>
                                <h3 className="font-serif text-3xl md:text-5xl font-light leading-[1.05] mb-5">
                                    {t.home.forArchTitle}
                                </h3>
                                <p className="text-sm md:text-base text-[#555] leading-relaxed md:leading-relaxed max-w-md mb-8">
                                    {t.home.forArchText}
                                </p>
                                <div className="text-[11px] tracking-luxury uppercase link-underline inline-flex items-center gap-2">
                                    {t.home.forArchCta} <ArrowRight size={14} />
                                </div>
                            </Link>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <section className="bg-white border-y border-[#EAE8E4] py-8 overflow-hidden">
                <div className="marquee">
                    <div className="marquee-track font-serif text-3xl md:text-5xl italic font-light text-[#111]">
                        {Array.from({ length: 2 }).map((_, idx) => (
                            <div key={idx} className="flex gap-16 pr-16">
                                <span>Renovering</span>
                                <span className="text-[#D9D4CD]">/</span>
                                <span>Tilbygning</span>
                                <span className="text-[#D9D4CD]">/</span>
                                <span>Sommerhus</span>
                                <span className="text-[#D9D4CD]">/</span>
                                <span>Rådgivning</span>
                                <span className="text-[#D9D4CD]">/</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0F0F0E] text-white py-32 md:py-48">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-8 md:col-start-3 text-center">
                        <h2 className="font-serif font-light text-4xl md:text-7xl leading-[1] tracking-tight">
                            {t.home.ctaTitle}
                        </h2>
                        <p className="mt-8 text-sm md:text-base text-white/70 max-w-xl mx-auto leading-relaxed md:leading-relaxed">
                            {t.home.ctaText}
                        </p>
                        <Link
                            to="/kontakt"
                            data-testid="home-cta-contact"
                            className="inline-flex items-center gap-3 mt-12 px-8 py-4 border border-white/70 hover:bg-white hover:text-[#0F0F0E] transition-colors text-[11px] tracking-luxury uppercase"
                        >
                            {t.home.ctaButton} <ArrowRight size={14} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </main>
    );
};

export default Home;
